import Header from "../../components/Header";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import { Autocomplete, Button, FormControl, TextField } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import useLoader from "../../hooks/useLoader";
import { formatDateToYYYYMMDD } from "../../Helper/functionHelper";
import { getWfhById, submitWfh } from "../../services/wfh";
import { useEffect } from "react";
import { applyWfhSchema } from "../../schemas/applyWfhSchema";

const dayOptions = [
  { label: "Full Day", value: "FD" },
  { label: "Half Day First", value: "HDF" },
  { label: "Half Day Second", value: "HDS" },
];

const WfhForm = () => {
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const { wfhId } = useParams();
  const {
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(applyWfhSchema),
    defaultValues: {
      reason: "",
      wfh_type: "",
      start_date: null,
      end_date: null,
    },
  });

  const startDate = watch("start_date");
  const endDate = watch("end_date");

  const submitWfhForm = async (data) => {
    try {
      showLoader();
      const formData = {
        start_date: formatDateToYYYYMMDD(data.start_date),
        end_date: formatDateToYYYYMMDD(data.end_date),
        wfh_type: data.wfh_type,
        reason: data.reason,
      };
      if (wfhId) {
        formData.wfh_id = wfhId;
      }
      try {
        const res = await submitWfh(formData);
        if (res.status === 200 && res.data?.status) {
          toast.success(res.data?.message || "WFH submitted!");
          navigate("/wfh");
        } else if (res.data?.message) {
          toast.error(res.data.message);
        } else {
          toast.error("Failed to submit WFH. Please try again.");
        }
      } catch (error) {
        if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error("Error submitting WFH. Please try again.");
        }
        console.error("Error submitting WFH:", error);
      }
    } catch (error) {
      console.error("Error submitting WFH:", error);
      toast.error(error.response?.data?.message || "Failed to submit WFH");
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    if (wfhId) {
      getWfhById(wfhId)
        .then((res) => {
          if (res.status === 200) {
            const { start_date, end_date, wfh_type, reason } = res.data.data;
            reset({
              start_date: new Date(start_date),
              end_date: new Date(end_date),
              wfh_type,
              reason,
            });
          }
        })
        .catch(() => {
          reset();
        });
    }
    // eslint-disable-next-line
  }, [wfhId]);

  const pageTitle = wfhId ? "Edit WFH" : "Apply WFH";

  return (
    <>
      <Header title={pageTitle} back="/wfh" />
      <div className="tab-profile mt-3">
        <div className="content-block">
          <div className="container-fluid">
            <form onSubmit={handleSubmit(submitWfhForm)}>
              <div className="row mt-3 row-gap-2">
                <div className="col-md-4">
                  <Controller
                    name="start_date"
                    control={control}
                    render={({ field }) => (
                      <>
                        <DatePicker
                          label="Start Date*"
                          {...field}
                          onChange={(date) => field.onChange(date)}
                          value={field.value || null}
                          format="dd MMM yyyy"
                          maxDate={endDate}
                          slotProps={{
                            textField: {
                              variant: "standard",
                              fullWidth: true,
                              error: !!errors.start_date,
                              helperText: errors.start_date?.message,
                            },
                          }}
                          disabled={isSubmitting}
                        />
                      </>
                    )}
                  />
                </div>
                <div className="col-md-4">
                  <Controller
                    name="end_date"
                    control={control}
                    render={({ field }) => (
                      <>
                        <DatePicker
                          label="End Date*"
                          {...field}
                          onChange={(date) => field.onChange(date)}
                          value={field.value || null}
                          format="dd MMM yyyy"
                          minDate={startDate}
                          slotProps={{
                            textField: {
                              variant: "standard",
                              fullWidth: true,
                              error: !!errors.end_date,
                              helperText: errors.end_date?.message,
                            },
                          }}
                          disabled={isSubmitting}
                        />
                      </>
                    )}
                  />
                </div>
                <div className="col-md-4">
                  <Controller
                    name="wfh_type"
                    control={control}
                    render={({ field }) => (
                      <FormControl
                        fullWidth
                        size="small"
                        sx={{
                          backgroundColor: "white",
                          marginTop: "7px",
                        }}
                      >
                        <Autocomplete
                          {...field}
                          options={dayOptions}
                          getOptionLabel={(option) =>
                            typeof option === "string" ? option : option.label
                          }
                          isOptionEqualToValue={(option, value) =>
                            option.value === value?.value
                          }
                          value={
                            dayOptions.find(
                              (item) => item.value === field.value
                            ) || null
                          }
                          onChange={(event, newValue) => {
                            field.onChange(newValue ? newValue.value : "");
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Wfh Type*"
                              size="small"
                              variant="standard"
                              error={!!errors.wfh_type}
                              helperText={errors.wfh_type?.message}
                            />
                          )}
                        />
                      </FormControl>
                    )}
                  />
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-md-12">
                  <Controller
                    name="reason"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Reason*"
                        {...field}
                        multiline
                        rows={4}
                        variant="standard"
                        fullWidth
                        error={!!errors.reason}
                        helperText={errors.reason?.message}
                        disabled={isSubmitting}
                      />
                    )}
                  />
                </div>
              </div>
              <div className="d-flex justify-content-end mt-2">
                <Button
                  type="submit"
                  variant="contained"
                  color="success"
                  disabled={isSubmitting}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : null
                  }
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default WfhForm;
